/**
 * One conversation with Buddy, wherever it is open (the Buddy page, the builder's assistant): Buddy's
 * status, the conversation (found by its draft key or its saved plan and created when there is none,
 * or read by its id when it was opened from the history), every running turn watched until it
 * finishes, and the trainer's intents sent.
 *
 * ── A message about a draft carries the draft ────────────────────────────────────────────────
 * Once there is a draft (Buddy's, or the plan the builder has open), every message travels with it
 * (R13): the host hands `send` what the draft is now, unfinished parts and all.
 *
 * What the page draws is one of five answers (`phase`): still reading, Buddy unavailable, the reads
 * failed, the conversation is gone (deleted, expired, or never this trainer's), or the conversation.
 * Once the conversation has been read it stays on screen: a later status failure or an
 * `ai_unavailable` refusal becomes a notice over the composer, because the built draft still opens
 * without Buddy. A conversation is read while Buddy is switched off or past the day's ceiling too:
 * its thread stays readable and its finished drafts still open.
 *
 * Refusals of a write turn into the state they describe (`aiRefusal`): a full window blocks the
 * composer with its reset time, `ai_unavailable` says Buddy is off, a conversation that moved is
 * read again and said so, and anything else says which act did not happen. One notice stands at a
 * time, the newest, and the next write that lands takes it down.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  activeTurnIds,
  aiConversationByIdQueryOptions,
  aiConversationQueryKey,
  aiConversationQueryOptions,
  aiPlanConversationQueryOptions,
  aiStatusQueryOptions,
  cancelTurn,
  aiRefusal,
  conversationFromWire,
  conversationHandle,
  mirrorConversationEntries,
  decisionKey,
  deleteConversation,
  importSpreadsheet,
  listSheets,
  meterFromStatus,
  planConversationHandle,
  rateTurn,
  recordDecisions,
  rememberBuddyRefused,
  resolveExercises,
  sendIntent,
  showTurn,
  turnFromWire,
  turnPollDelayMs,
  watchTurn,
  withTurn,
  type AiChangeDecision,
  type AiConversation,
  type AiDraftSnapshot,
  type AiImportSchedulingMode,
  type AiIntent,
  type AiNamedDecision,
  type AiRefusal,
  type AiSheetListing,
  type AiUsageMeter,
  type AiWriteOutcome,
} from '~/entities/ai-conversation';
import type { TrainWithMeModulesAiDtosResponsesAiConversationResponse as ConversationDto } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { preferenceStore } from '~/shared/lib/preferenceStore';

import { isChoiceRefusal } from './importWording';

/** Which act a notice is about: each is worded for what did or did not happen. */
export type BuddyNotice =
  | 'message'
  | 'answers'
  | 'skipQuestions'
  | 'approveOutline'
  | 'retry'
  | 'stop'
  | 'dismiss'
  | 'reread'
  | 'pick'
  | 'open'
  | 'stale'
  | 'staleAction'
  | 'notWaiting'
  | 'answerInvalid'
  | 'feedback'
  | 'delete'
  | 'unavailable'
  | 'decisionHeld'
  | 'pickReread'
  | 'import'
  | 'listing'
  | 'busy'
  | 'snapshotInvalid'
  | 'snapshotTooLarge'
  | 'decisionInvalid'
  | 'decisions';

/** The wording of each notice. */
const NOTICE_COPY: Readonly<Record<BuddyNotice, string>> = {
  message: 'web:aiPlan.notice.message',
  answers: 'web:aiPlan.notice.answers',
  skipQuestions: 'web:aiPlan.notice.skipQuestions',
  approveOutline: 'web:aiPlan.notice.approveOutline',
  retry: 'web:aiPlan.notice.retry',
  stop: 'web:aiPlan.notice.stop',
  dismiss: 'web:aiPlan.notice.dismiss',
  reread: 'web:aiPlan.notice.reread',
  pick: 'web:aiPlan.notice.pick',
  open: 'web:aiPlan.notice.open',
  stale: 'web:aiPlan.notice.stale',
  staleAction: 'web:aiPlan.notice.staleAction',
  notWaiting: 'web:aiPlan.notice.notWaiting',
  answerInvalid: 'web:aiPlan.notice.answerInvalid',
  unavailable: 'web:aiPlan.notice.unavailable',
  feedback: 'web:aiPlan.notice.feedback',
  delete: 'web:aiPlan.notice.delete',
  decisionHeld: 'web:aiPlan.notice.decisionHeld',
  pickReread: 'web:aiPlan.notice.pickReread',
  import: 'web:aiPlan.notice.import',
  listing: 'web:aiPlan.notice.listing',
  busy: 'web:aiPlan.notice.busy',
  snapshotInvalid: 'web:aiPlan.notice.snapshotInvalid',
  snapshotTooLarge: 'web:aiPlan.notice.snapshotTooLarge',
  decisionInvalid: 'web:aiPlan.notice.decisionInvalid',
  decisions: 'web:aiPlan.notice.decisions',
};

/** What the draft is as a message leaves (R13): none yet, or the snapshot to send. */
export type DraftForMessage = { readonly kind: 'none' } | { readonly kind: 'ready'; readonly snapshot: AiDraftSnapshot };

const NO_DRAFT: DraftForMessage = { kind: 'none' };

/** What became of an import: queued, or refused. */
export type ImportOutcome = { readonly outcome: 'sent' } | { readonly outcome: 'refused'; readonly refusal: AiRefusal };

/** The refusals of a sheet choice, which the picker words itself. */
function refusesTheChoice(refusal: AiRefusal): boolean {
  return refusal.kind === 'import' && isChoiceRefusal(refusal.reason);
}

export type BuddyPhase =
  | { readonly phase: 'pending' }
  | { readonly phase: 'unavailable' }
  | { readonly phase: 'failed'; readonly retry: () => void }
  /** Not there for this trainer: the conversation (deleted, expired, never theirs), or its client. */
  | { readonly phase: 'gone'; readonly what: 'conversation' | 'client' }
  | {
      readonly phase: 'ready';
      readonly conversation: AiConversation;
      /** The template this conversation is bound to, once its draft was saved. */
      readonly boundTemplateId: number | null;
    };

export interface BuddyConversation {
  readonly state: BuddyPhase;
  /** Send an intent; a message about a draft goes with `draft` (R13). */
  readonly send: (intent: AiIntent, draft?: DraftForMessage) => Promise<boolean>;
  readonly stop: (turnId: number) => void;
  /** Cancel the outline waiting for approval (R6): nothing is built from it, here or after a reload. */
  readonly dismissOutline: (turnId: number) => void;
  /**
   * Decisions on names of draft `turnId`, one or several in one write. Answers the draft's revision
   * they left, or `null` when they were not taken.
   */
  readonly decide: (turnId: number, decisions: readonly AiNamedDecision[]) => Promise<number | null>;
  /** The names whose decisions are on their way. */
  readonly deciding: ReadonlySet<string>;
  /** What the last act could not do, already worded; `null` when nothing failed. */
  readonly notice: string | null;
  /**
   * Say an act of the page's did not happen: the draft could not open, or a decision waits for the
   * session's edits to be saved. The next write that lands takes it down.
   */
  readonly report: (notice: 'open' | 'decisionHeld') => void;
  /**
   * Rate a draft, with a comment or without (R47); the chosen thumb is held in the conversation as
   * the server answered. Answers whether the rating was taken.
   */
  readonly rate: (turnId: number, helpful: boolean, comment: string | null) => Promise<boolean>;
  /** Delete the conversation (R45); answers whether it is gone. */
  readonly remove: () => Promise<boolean>;
  /**
   * The sheets of a file (R27). A refusal that is not the file's (Buddy switched off, no answer) is
   * this conversation's notice; the file's own refusal is the caller's to show where it was dropped.
   */
  readonly listFile: (file: File) => Promise<AiSheetListing>;
  /**
   * The chosen sheets of a file imported into a draft (R27). A refusal of the choice itself is the
   * picker's to show (`ai_import_sheet_too_large`, `ai_import_sheets_invalid`); any other is a notice.
   */
  readonly importFile: (file: File, sheets: readonly string[], schedulingMode: AiImportSchedulingMode | null) => Promise<ImportOutcome>;
  /** The trainer's decisions on changes of proposal `turnId` (R53); answers whether they were recorded. */
  readonly decideChanges: (turnId: number, snapshotVersion: string, decisions: ReadonlyMap<string, AiChangeDecision>) => Promise<boolean>;
}

/**
 * Which conversation: the one this device is in (its draft key; created on the first read when the
 * server has none), or one from the history, by its id. A key still `null` is a conversation for a
 * client whose read has not answered yet: nothing is read or created for it until it has.
 */
export type BuddyConversationSource =
  | { readonly kind: 'key'; readonly draftKey: string | null; readonly clientId: number | null }
  | { readonly kind: 'id'; readonly conversationId: number }
  /** The conversation about a saved library plan, opened from the builder (R2, R13). */
  | { readonly kind: 'plan'; readonly planTemplateId: number };

function sourceHandle(source: BuddyConversationSource): string {
  switch (source.kind) {
    case 'key':
      return source.draftKey ?? '';
    case 'id':
      return conversationHandle(source.conversationId);
    case 'plan':
      return planConversationHandle(source.planTemplateId);
  }
}

export function useBuddyConversation(source: BuddyConversationSource, userId: string): BuddyConversation {
  const { t } = useTranslation();
  const client = useQueryClient();
  const contentLanguage = useContentLanguage();
  const status = useQuery(aiStatusQueryOptions());
  const available = status.data?.available === true;
  // A conversation is readable while Buddy is this trainer's, running or not: a switched-off Buddy
  // and the day's ceiling stop new work, not the reading of what is there.
  const reason = status.data?.unavailableReason;
  const readable = available || reason === 'switchedOff' || reason === 'serviceCeiling';
  // The cache handle every write and the turn watch keep: the draft key, the plan's, or the id's.
  const draftKey = sourceHandle(source);
  const conversation = useQuery({
    ...(source.kind === 'key'
      ? aiConversationQueryOptions(draftKey, contentLanguage, source.clientId, available)
      : source.kind === 'plan'
        ? aiPlanConversationQueryOptions(source.planTemplateId, contentLanguage, available)
        : aiConversationByIdQueryOptions(source.conversationId)),
    enabled: readable && draftKey !== '',
  });
  const dto = conversation.data;
  const conversationId = dto?.id ?? null;

  // A refusal the status read has not caught up with yet: held until the next status answer.
  const [limit, setLimit] = useState<{ readonly meter: AiUsageMeter; readonly statusAt: number } | null>(null);
  const [switchedOff, setSwitchedOff] = useState(false);
  const [notice, setNotice] = useState<BuddyNotice | null>(null);
  const [deciding, setDeciding] = useState<ReadonlySet<string>>(new Set());
  // The page keeps one of these across conversations (the history rail beside it stays mounted),
  // so what belongs to ONE conversation is dropped when another is opened. A reached limit and a
  // switched-off Buddy are the account's and stay.
  const [openHandle, setOpenHandle] = useState(draftKey);
  if (openHandle !== draftKey) {
    setOpenHandle(draftKey);
    setNotice(null);
    setDeciding(new Set());
  }
  // A conversation opened by its id and by its draft key is one conversation: both entries say the same.
  useEffect(() => mirrorConversationEntries(client), [client]);

  // One send at a time: Enter pressed twice sends once.
  const sending = useRef(false);
  // Decisions go one after another, so the server answers them in the order they were made and the
  // newest answer (which carries every decision so far) is the one that lands last.
  const decisions = useRef<Promise<void>>(Promise.resolve());

  // Watch every turn the server is still working on: after a send, and on a return to a
  // conversation left running in a closed tab (R12). A watch whose read failed is started again
  // after a back-off while the conversation still names the turn (`restarts`).
  const watched = activeTurnIds(dto).join(',');
  const [restarts, setRestarts] = useState(0);
  useEffect(() => {
    if (conversationId === null || watched === '') return;
    let retry: number | undefined;
    const stops = watched.split(',').map((id) =>
      watchTurn(client, draftKey, conversationId, Number(id), () => {
        window.clearTimeout(retry);
        retry = window.setTimeout(() => {
          setRestarts((count) => count + 1);
        }, turnPollDelayMs(restarts + 1));
      }),
    );
    return () => {
      window.clearTimeout(retry);
      stops.forEach((stop) => {
        stop();
      });
    };
  }, [client, draftKey, conversationId, watched, restarts]);

  const fromStatus = meterFromStatus(status.data);
  const meter = limit !== null && limit.statusAt >= status.dataUpdatedAt && !fromStatus.blocked ? limit.meter : fromStatus;

  // A full window frees up at its reset time: read the status again then, so the composer opens
  // without the trainer reloading.
  const resetsAt = meter.blocked ? meter.resetsAt : null;
  useEffect(() => {
    if (resetsAt === null) return;
    const wait = Math.max(0, Date.parse(resetsAt) - Date.now()) + 1_000;
    const timer = window.setTimeout(() => {
      void client.invalidateQueries({ queryKey: aiStatusQueryOptions().queryKey });
    }, wait);
    return () => {
      window.clearTimeout(timer);
    };
  }, [client, resetsAt]);

  // The conversation read refused as unavailable while the status still said otherwise.
  const readRefused =
    conversation.error instanceof ApiError &&
    aiRefusal({ status: conversation.error.status, code: conversation.error.code, problem: conversation.error.problem })
      .kind === 'unavailable';
  // A refused call takes back the offer this device remembers for the create window, and the
  // status is read again for the reason.
  const refusedAsUnavailable = switchedOff || readRefused;
  useEffect(() => {
    if (!refusedAsUnavailable) return;
    rememberBuddyRefused(preferenceStore(), userId);
    void client.invalidateQueries({ queryKey: aiStatusQueryOptions().queryKey });
  }, [refusedAsUnavailable, userId, client]);

  // The conversation is owed a read: a finished turn was shown without one (its read failed), or
  // the last read itself failed. It is read again, with the turn watch's back-off, until it lands;
  // that read brings what the server now waits on, and any turn it queued behind the finished one,
  // which the watch above then picks up.
  const behind = dto !== undefined && conversation.isStale;
  const owed = behind && !conversation.isFetching;
  const [rereads, setRereads] = useState(0);
  if (!behind && rereads !== 0) setRereads(0);
  useEffect(() => {
    if (!owed) return;
    const timer = window.setTimeout(() => {
      setRereads((count) => count + 1);
      void client.refetchQueries({ queryKey: aiConversationQueryKey(draftKey), exact: true });
    }, turnPollDelayMs(rereads));
    return () => {
      window.clearTimeout(timer);
    };
  }, [owed, rereads, client, draftKey]);
  const rereading = behind && (owed || rereads > 0);

  const refused = (refusal: AiRefusal, act: BuddyNotice) => {
    switch (refusal.kind) {
      case 'unavailable':
        setSwitchedOff(true);
        setNotice('unavailable');
        return;
      case 'limit':
        setLimit({ meter: refusal.meter, statusAt: status.dataUpdatedAt });
        setNotice(null);
        void client.invalidateQueries({ queryKey: aiStatusQueryOptions().queryKey });
        return;
      case 'stale':
      case 'notWaiting':
      case 'answerInvalid':
      case 'decisionInvalid':
        // Only a message keeps something to send again; every other act is simply tried again.
        setNotice(refusal.kind === 'stale' && act !== 'message' ? 'staleAction' : refusal.kind);
        void client.invalidateQueries({ queryKey: aiConversationQueryKey(draftKey) });
        return;
      case 'busy':
      case 'snapshotInvalid':
      case 'snapshotTooLarge':
        setNotice(refusal.kind);
        return;
      case 'import':
        // A refused file is drawn where it was dropped, with how to fix it; here it is only the act.
        setNotice(act === 'listing' ? 'listing' : 'import');
        return;
      // No act here records a dictation; its refusals are the composer's own (`useDictation`).
      case 'dictation':
      case 'failed':
        setNotice(act);
    }
  };


  // A turn that came back finished (a dismissed outline, a stopped turn, an approval answered with
  // the build it already has) is shown from a fresh conversation read, as the watch shows one: what
  // waits after it is the conversation's to say, and it arrives in the same paint. A decision on an
  // exercise changes nothing that waits, so its turn is merged as it is (`merge`).
  const land = async (outcome: AiWriteOutcome, act: BuddyNotice, how: 'show' | 'merge' = 'show'): Promise<boolean> => {
    if (outcome.outcome === 'sent') {
      if (how === 'merge') {
        client.setQueryData<ConversationDto>(aiConversationQueryKey(draftKey), (current) =>
          current === undefined ? current : withTurn(current, outcome.turn),
        );
      } else {
        await showTurn(client, draftKey, outcome.turn);
      }
      setNotice(null);
      return true;
    }
    refused(outcome.refusal, act);
    return false;
  };

  // A promise chain rather than `try`/`finally`, which the React Compiler cannot lower: the hook
  // would be left uncompiled.
  const send = (intent: AiIntent, carried: DraftForMessage = NO_DRAFT): Promise<boolean> => {
    if (conversationId === null || sending.current) return Promise.resolve(false);
    // Only words travel with the draft: answers, a skip, an approval and a retry are about the turn.
    const draft = intent.kind === 'message' ? carried : NO_DRAFT;
    sending.current = true;
    return sendIntent(conversationId, intent, draft.kind === 'ready' ? draft.snapshot : null)
      .then((outcome) => land(outcome, intent.kind))
      .finally(() => {
        sending.current = false;
      });
  };

  const importFile = (file: File, sheets: readonly string[], schedulingMode: AiImportSchedulingMode | null): Promise<ImportOutcome> => {
    if (conversationId === null || sending.current) return Promise.resolve({ outcome: 'refused', refusal: { kind: 'failed' } });
    sending.current = true;
    return importSpreadsheet(conversationId, file, sheets, schedulingMode, crypto.randomUUID())
      .then(async (outcome): Promise<ImportOutcome> => {
        if (outcome.outcome === 'sent') {
          await land(outcome, 'import');
          return { outcome: 'sent' };
        }
        if (!refusesTheChoice(outcome.refusal)) refused(outcome.refusal, 'import');
        return { outcome: 'refused', refusal: outcome.refusal };
      })
      .finally(() => {
        sending.current = false;
      });
  };

  const listFile = (file: File): Promise<AiSheetListing> =>
    listSheets(file).then((listing) => {
      if (listing.outcome === 'listed') setNotice(null);
      else if (listing.refusal.kind !== 'import') refused(listing.refusal, 'listing');
      return listing;
    });

  // A decision changes nothing that waits, apart from an apply, which ends the wait: the turn is
  // merged as it is, and the conversation is read for what the server waits on now.
  const decideChanges = (
    turnId: number,
    snapshotVersion: string,
    decided: ReadonlyMap<string, AiChangeDecision>,
  ): Promise<boolean> => {
    if (conversationId === null || decided.size === 0) return Promise.resolve(false);
    return recordDecisions(conversationId, turnId, snapshotVersion, decided).then(async (outcome) => {
      const landed = await land(outcome, 'decisions', 'merge');
      if (landed && [...decided.values()].some((decision) => decision !== 'rejected')) {
        void client.invalidateQueries({ queryKey: aiConversationQueryKey(draftKey), exact: true });
      }
      return landed;
    });
  };

  const stop = (turnId: number) => {
    if (conversationId === null) return;
    void cancelTurn(conversationId, turnId).then((outcome) => land(outcome, 'stop'));
  };

  // The same route as a stop: on a finished outline the server dismisses it.
  const dismissOutline = (turnId: number) => {
    if (conversationId === null) return;
    void cancelTurn(conversationId, turnId).then((outcome) => land(outcome, 'dismiss'));
  };

  const decide = (turnId: number, named: readonly AiNamedDecision[]): Promise<number | null> => {
    if (conversationId === null || named.length === 0) return Promise.resolve(null);
    const keys = named.map(({ name }) => decisionKey(turnId, name));
    setDeciding((current) => new Set([...current, ...keys]));
    const decided = decisions.current.then(async (): Promise<number | null> => {
      let outcome = await resolveExercises(conversationId, turnId, named);
      // The draft kept moving while the decision was being written (409 `ai_draft_stale`): the
      // conversation is read again and the trainer's decision sent once more, on that draft.
      const moved = outcome.outcome === 'refused' && outcome.refusal.kind === 'stale';
      if (moved) {
        await client.refetchQueries({ queryKey: aiConversationQueryKey(draftKey), exact: true });
        outcome = await resolveExercises(conversationId, turnId, named);
      }
      setDeciding((current) => {
        const next = new Set(current);
        for (const key of keys) next.delete(key);
        return next;
      });
      if (!(await land(outcome, 'pick', 'merge'))) return null;
      if (moved) setNotice('pickReread');
      const turn = outcome.outcome === 'sent' ? turnFromWire(outcome.turn) : null;
      return turn?.result?.kind === 'build' ? turn.result.result.revision : null;
    });
    decisions.current = decided.then(() => undefined);
    return decided;
  };

  // A conversation by its id that the server does not have for this trainer: deleted, expired, or
  // never theirs. Said whatever an earlier read left in the cache. A new conversation is refused
  // for a client the trainer no longer coaches (404 `ai_client_out_of_reach`).
  const refusedRead = conversation.error instanceof ApiError ? conversation.error : null;
  const gone: 'conversation' | 'client' | null =
    refusedRead?.status === 404 && refusedRead.code === 'ai_client_out_of_reach'
      ? 'client'
      : source.kind === 'id' && (refusedRead?.status === 404 || refusedRead?.status === 403)
        ? 'conversation'
        : null;

  const state: BuddyPhase = (() => {
    if (gone !== null) return { phase: 'gone', what: gone };
    // The conversation is read again on every visit (it may have been bound since); a copy left
    // in the cache from earlier in the session is not drawn until that read answers, so the thread
    // never stands for a moment before the fresh conversation replaces it.
    if (dto !== undefined && !conversation.isFetchedAfterMount && conversation.isFetching) return { phase: 'pending' };
    // While Buddy cannot work, a conversation with nothing in it is nothing to read.
    const off = status.data !== undefined && !available;
    if (dto !== undefined && (!off || (dto.turns ?? []).length > 0)) {
      return { phase: 'ready', conversation: conversationFromWire(dto, meter), boundTemplateId: dto.planTemplateId ?? null };
    }
    if (off && (dto !== undefined || (refusedRead?.status === 404 && source.kind === 'key'))) return { phase: 'unavailable' };
    if (refusedAsUnavailable || (status.data !== undefined && !readable)) return { phase: 'unavailable' };
    if (status.isError || conversation.isError) {
      return {
        phase: 'failed',
        retry: () => {
          void status.refetch();
          void conversation.refetch();
        },
      };
    }
    return { phase: 'pending' };
  })();

  return {
    state,
    send,
    stop,
    dismissOutline,
    decide,
    deciding,
    notice: (() => {
      const shown = notice ?? (dto !== undefined && status.data !== undefined && !available ? 'unavailable' : rereading ? 'reread' : null);
      return shown === null ? null : t(NOTICE_COPY[shown]);
    })(),
    listFile,
    importFile,
    decideChanges,
    report: (act) => {
      setNotice(act);
    },
    rate: async (turnId, helpful, comment) => {
      if (conversationId === null) return false;
      const rated = await rateTurn(conversationId, turnId, helpful, comment);
      if (!rated) {
        setNotice('feedback');
        return false;
      }
      setNotice(null);
      client.setQueryData<ConversationDto>(aiConversationQueryKey(draftKey), (current) =>
        current === undefined
          ? current
          : { ...current, turns: (current.turns ?? []).map((turn) => (turn.id === turnId ? { ...turn, feedbackHelpful: helpful } : turn)) },
      );
      return true;
    },
    remove: async () => {
      if (conversationId === null) return false;
      const gone = await deleteConversation(conversationId);
      if (!gone) setNotice('delete');
      else client.removeQueries({ queryKey: aiConversationQueryKey(draftKey) });
      return gone;
    },
  };
}
